import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Eye, Globe, Layers } from "lucide-react";

import { AppIcon } from "../../components/apps/AppIcon";
import { isAndroidPackageName } from "../../lib/apps/androidAppIcon";
import { iconAppsForTarget, aggregatePickerCounts } from "../../lib/insights/smartBlockSelection";
import {
  canPickAndroidApps,
  canPickScreenTimeApps,
  presentCombinedSelectionDisplay,
} from "../../lib/native/rules";
import {
  targetContentSummary,
  selectionSummary,
  type TargetContentSummaryOptions,
} from "../../lib/rules/ruleDerivation";
import type { Target, TargetApp, TargetSelection } from "../../lib/rules/ruleTypes";
import { resolveTargetDomains, sanitizeDomainCategories } from "../../lib/rules/websiteCategories";
import { websiteCategoryLabel } from "./WebsiteCategoryPickerModal";
import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";

import { computeFitVisibleCount } from "./previewFit";

const DEFAULT_COLOR = "#7ab89a";
export const MAX_VISIBLE_APPS_COMPACT = 5;
export const MAX_VISIBLE_APPS_RULE = 7;
export const MAX_VISIBLE_DOMAINS_COMPACT = 4;
export const MAX_VISIBLE_DOMAINS_RULE = 4;

/** Includes typed apps/domains *and* the picker selection counts (never "Empty" for a picker target). */
export function targetContents(target: Target, options?: TargetContentSummaryOptions): string {
  return targetContentSummary(target, options);
}

export function selectionHasPickerAppsOrCategories(selection: TargetSelection | undefined): boolean {
  if (!selection) return false;
  return selection.applicationCount + selection.categoryCount > 0;
}

export function selectionHasPickerWebDomains(selection: TargetSelection | undefined): boolean {
  if (!selection) return false;
  return selection.webDomainCount > 0;
}

export function targetHasPickerSelection(target: Target): boolean {
  const selection = target.selection;
  if (!selection?.id) return false;
  return selectionHasPickerAppsOrCategories(selection) || selectionHasPickerWebDomains(selection);
}

export function uniquePickerSelectionIds(targets: Target[]): string[] {
  const ids = new Set<string>();
  for (const target of targets) {
    if (targetHasPickerSelection(target) && target.selection?.id) {
      ids.add(target.selection.id);
    }
  }
  return [...ids];
}

export function shouldShowSelectionView(targets: Target | Target[]): boolean {
  if (!canPickScreenTimeApps()) return false;
  const list = Array.isArray(targets) ? targets : [targets];
  return list.some(targetHasPickerSelection);
}

async function presentSelectionViewForTargets(targets: Target | Target[]): Promise<void> {
  const list = Array.isArray(targets) ? targets : [targets];
  await presentCombinedSelectionDisplay(uniquePickerSelectionIds(list));
}

type SelectionViewButtonProps = {
  targets: Target | Target[];
  label: string;
  ariaLabel: string;
  inline?: boolean;
};

export function SelectionViewButton({ targets, label, ariaLabel, inline = false }: SelectionViewButtonProps) {
  if (!shouldShowSelectionView(targets)) return null;

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        void presentSelectionViewForTargets(targets);
      }}
      className={
        inline
          ? "flex shrink-0 items-center gap-1 text-xs font-semibold text-[oklch(72%_0.09_155)] transition-colors active:opacity-80"
          : "flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-[oklch(72%_0.09_155)] transition-colors active:bg-[oklch(20%_0.02_165)]"
      }
      aria-label={ariaLabel}
    >
      <Eye size={inline ? 12 : 13} aria-hidden />
      {label}
    </button>
  );
}

type SummaryWithViewRowProps = {
  summary: string | null;
  targets: Target | Target[];
};

export function SummaryWithViewRow({ summary, targets }: SummaryWithViewRowProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const showView = shouldShowSelectionView(targets);

  if (!summary && !showView) return null;

  return (
    <div className="flex min-w-0 items-center gap-2 text-xs text-[oklch(64%_0.025_155)]">
      {summary ? <span className="min-w-0 truncate">{summary}</span> : null}
      {showView ? (
        <SelectionViewButton
          targets={targets}
          label={t.rules.view}
          ariaLabel={t.rules.viewFullBlockedListAria}
          inline
        />
      ) : null}
    </div>
  );
}

export function dedupeAppsFromTargets(targets: Target[]): TargetApp[] {
  const seen = new Set<string>();
  const result: TargetApp[] = [];
  for (const target of targets) {
    for (const app of target.apps) {
      if (seen.has(app.key)) continue;
      seen.add(app.key);
      result.push(app);
    }
  }
  return result;
}

export function dedupeDomainsFromTargets(targets: Target[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const target of targets) {
    for (const domain of resolveTargetDomains(target)) {
      const key = domain.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(domain);
    }
  }
  return result;
}

/** Rule card text summary — Android uses deduped typed apps; iOS keeps picker aggregation. */
export function ruleBlockSummaryText(ruleTargets: Target[]): string | null {
  if (ruleTargets.length === 0) return null;

  if (canPickAndroidApps()) {
    const iconApps = dedupeAppsFromTargets(ruleTargets).filter((app) =>
      isAndroidPackageName(app.appIdentifier),
    );
    if (iconApps.length > 0) {
      const { categoryCount, webDomainCount } = aggregatePickerCounts(ruleTargets);
      const typedSiteCount = dedupeDomainsFromTargets(ruleTargets).length;
      const parts: string[] = [];
      parts.push(`${iconApps.length} ${iconApps.length === 1 ? "app" : "apps"}`);
      if (categoryCount > 0) {
        parts.push(`${categoryCount} ${categoryCount === 1 ? "category" : "categories"}`);
      }
      const siteCount = typedSiteCount + webDomainCount;
      if (siteCount > 0) {
        parts.push(`${siteCount} ${siteCount === 1 ? "website" : "websites"}`);
      }
      return parts.join(" · ");
    }
  }

  let pickerAppCount = 0;
  let pickerCategoryCount = 0;
  let pickerWebCount = 0;
  const seenSelections = new Set<string>();
  for (const target of ruleTargets) {
    const selection = target.selection;
    if (!selection?.id || seenSelections.has(selection.id)) continue;
    seenSelections.add(selection.id);
    pickerAppCount += selection.applicationCount;
    pickerCategoryCount += selection.categoryCount;
    pickerWebCount += selection.webDomainCount;
  }
  return aggregatedPickerSummary(pickerAppCount, pickerCategoryCount, pickerWebCount);
}

export function TargetPreviewMeta({
  target,
  compact = false,
  dedupeTypedAndPickerApps,
  showIcons = true,
}: {
  target: Target;
  compact?: boolean;
  dedupeTypedAndPickerApps?: boolean;
  showIcons?: boolean;
}) {
  const summaryOptions: TargetContentSummaryOptions = {
    dedupeTypedAndPickerApps,
    androidNative: canPickAndroidApps(),
  };
  const iconApps = showIcons ? iconAppsForTarget(target) : [];

  return (
    <div className="min-w-0 flex-1">
      <div className={`truncate font-semibold text-[oklch(90%_0.02_150)] ${compact ? "text-sm" : "text-sm"}`}>{target.name}</div>
      {!compact ? (
        <div className="grid gap-1.5">
          <div className="truncate text-xs text-[oklch(64%_0.025_155)]">
            {targetContents(target, summaryOptions)}
          </div>
          {iconApps.length > 0 ? (
            <PreviewAppIconsRow apps={iconApps} maxVisible={MAX_VISIBLE_APPS_COMPACT} size="compact" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type PreviewAppIconsRowProps = {
  apps: TargetApp[];
  maxVisible: number;
  size?: "compact" | "default";
};

export const COMPACT_GLOBE_FALLBACK_SHELL_CLASS = "shrink-0";
export const COMPACT_GLOBE_FALLBACK_ICON_SIZE = 20;

const PREVIEW_ELLIPSIS = "…";

type UseFitPreviewItemsOptions = {
  itemCount: number;
  maxVisible: number;
  gapPx: number;
};

function useFitPreviewItems({ itemCount, maxVisible, gapPx }: UseFitPreviewItemsOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const ellipsisRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(() => Math.min(itemCount, maxVisible));

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    const ellipsis = ellipsisRef.current;
    if (!container || !measure) return;

    const compute = () => {
      const itemElements = Array.from(measure.children).filter(
        (element) => element !== ellipsis,
      ) as HTMLElement[];
      const itemWidths = itemElements.map((element) => element.offsetWidth);
      const nextVisible = computeFitVisibleCount({
        itemWidths,
        itemCount,
        maxVisible,
        gapPx,
        ellipsisWidth: ellipsis?.offsetWidth ?? 12,
        availableWidth: container.clientWidth,
      });
      setVisibleCount((current) => (current === nextVisible ? current : nextVisible));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [gapPx, itemCount, maxVisible]);

  return {
    containerRef,
    measureRef,
    ellipsisRef,
    visibleCount,
    hasHidden: itemCount > visibleCount,
  };
}

type CollisionSafePreviewRowProps = {
  itemCount: number;
  maxVisible: number;
  gapPx: number;
  rowClassName: string;
  ellipsisClassName: string;
  renderItem: (index: number) => ReactNode;
  itemKey: (index: number) => string;
};

function CollisionSafePreviewRow({
  itemCount,
  maxVisible,
  gapPx,
  rowClassName,
  ellipsisClassName,
  renderItem,
  itemKey,
}: CollisionSafePreviewRowProps) {
  const { containerRef, measureRef, ellipsisRef, visibleCount, hasHidden } = useFitPreviewItems({
    itemCount,
    maxVisible,
    gapPx,
  });
  const measureCount = Math.min(itemCount, maxVisible);

  if (itemCount === 0) return null;

  return (
    <div ref={containerRef} className="relative min-w-0 w-full overflow-hidden">
      <div
        ref={measureRef}
        aria-hidden
        className={`pointer-events-none absolute inset-x-0 top-0 flex items-center opacity-0 ${rowClassName}`}
      >
        {Array.from({ length: measureCount }, (_, index) => (
          <span key={itemKey(index)} className="shrink-0">
            {renderItem(index)}
          </span>
        ))}
        <span ref={ellipsisRef} className={ellipsisClassName}>
          {PREVIEW_ELLIPSIS}
        </span>
      </div>
      <div className={`flex min-w-0 items-center overflow-hidden ${rowClassName}`}>
        {Array.from({ length: visibleCount }, (_, index) => (
          <span key={itemKey(index)} className="shrink-0">
            {renderItem(index)}
          </span>
        ))}
        {hasHidden ? (
          <span className={ellipsisClassName} aria-hidden>
            {PREVIEW_ELLIPSIS}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function PreviewAppIconsRow({ apps, maxVisible, size = "default" }: PreviewAppIconsRowProps) {
  const compact = size === "compact";
  const appIconInner = compact ? 18 : 16;
  const appIconOuter = compact ? 20 : 18;
  const rowGapClass = compact ? "gap-1.5" : "gap-1";
  const ellipsisClass = compact
    ? "ml-0.5 shrink-0 text-[11px] font-semibold text-[oklch(58%_0.025_155)]"
    : "ml-0.5 shrink-0 text-[11px] font-semibold text-[oklch(58%_0.025_155)]";
  const gapPx = compact ? 6 : 4;

  if (apps.length === 0) return null;

  return (
    <CollisionSafePreviewRow
      itemCount={apps.length}
      maxVisible={maxVisible}
      gapPx={gapPx}
      rowClassName={rowGapClass}
      ellipsisClassName={ellipsisClass}
      itemKey={(index) => apps[index]?.key ?? `app-${index}`}
      renderItem={(index) => {
        const app = apps[index];
        if (!app) return null;
        return (
          <AppIcon
            displayName={app.label}
            appIdentifier={app.appIdentifier}
            category={app.category}
            color={DEFAULT_COLOR}
            size={appIconInner}
            iconSize={appIconOuter}
          />
        );
      }}
    />
  );
}

type PreviewDomainPillsRowProps = {
  domains: string[];
  maxVisible: number;
  align?: "start" | "end";
  maxWidthClass?: string;
  size?: "compact" | "default";
};

export function PreviewDomainPillsRow({
  domains,
  maxVisible,
  align = "end",
  maxWidthClass = "max-w-[9.5rem]",
  size = "default",
}: PreviewDomainPillsRowProps) {
  const compact = size === "compact";
  const pillClass = compact
    ? "liquid-glass-chip min-w-0 max-w-[5rem] shrink gap-0.5 px-2 py-0.5 text-[11px] leading-none text-[oklch(76%_0.025_155)]"
    : "liquid-glass-chip min-w-0 max-w-[4.75rem] shrink gap-0.5 px-1.5 py-0.5 text-[10px] leading-none text-[oklch(76%_0.025_155)]";
  const globeSize = compact ? 11 : 9;
  const ellipsisClass = compact
    ? "shrink-0 text-[11px] font-semibold text-[oklch(58%_0.025_155)]"
    : "shrink-0 text-[10px] font-semibold text-[oklch(58%_0.025_155)]";
  const rowGapClass = "gap-0.5";
  const gapPx = 2;
  const widthClass = maxWidthClass === "max-w-full" ? "w-full min-w-0" : maxWidthClass;

  if (domains.length === 0) return null;

  return (
    <div className={`${widthClass} ${align === "end" ? "ml-auto" : ""}`}>
      <CollisionSafePreviewRow
        itemCount={domains.length}
        maxVisible={maxVisible}
        gapPx={gapPx}
        rowClassName={`${rowGapClass} ${align === "end" ? "justify-end" : "justify-start"}`}
        ellipsisClassName={ellipsisClass}
        itemKey={(index) => domains[index] ?? `domain-${index}`}
        renderItem={(index) => {
          const domain = domains[index];
          if (!domain) return null;
          return (
            <span className={pillClass} title={domain}>
              <Globe size={globeSize} className="shrink-0" aria-hidden />
              <span className="truncate">{domain}</span>
            </span>
          );
        }}
      />
    </div>
  );
}

/**
 * Right-aligned View affordance for the **Edit rule** drawer target rows.
 * Picker summary lives once under the name via TargetPreviewMeta.
 */
export function TargetPreviewAssets({ target }: { target: Target }) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);

  if (!shouldShowSelectionView(target)) return null;

  return (
    <div className="flex shrink-0 items-start pt-0.5">
      <SelectionViewButton
        targets={target}
        label={t.rules.view}
        ariaLabel={t.rules.viewFullBlockedListAria}
      />
    </div>
  );
}

/** Left-aligned app icon row for list cards — manually typed apps only. */
export function CardAppIcons({ typedApps }: { typedApps: TargetApp[] }) {
  if (typedApps.length === 0) return null;
  return <PreviewAppIconsRow apps={typedApps} maxVisible={MAX_VISIBLE_APPS_COMPACT} size="compact" />;
}

/** Left-aligned websites row for list cards — manually typed domains only. */
export function CardWebsites({ typedDomains }: { typedDomains: string[] }) {
  if (typedDomains.length === 0) return null;
  return (
    <PreviewDomainPillsRow
      domains={typedDomains}
      maxVisible={MAX_VISIBLE_DOMAINS_RULE}
      align="start"
      maxWidthClass="max-w-full"
      size="compact"
    />
  );
}

/** Left-aligned website category pills for list cards. */
export function CardWebsiteCategories({ categoryIds }: { categoryIds: string[] }) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const categories = sanitizeDomainCategories(categoryIds);
  if (categories.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap gap-1.5">
      {categories.map((categoryId) => (
        <span
          key={categoryId}
          className="liquid-glass-badge inline-flex min-w-0 max-w-[8rem] shrink items-center gap-0.5 px-2 py-0.5 text-[11px] leading-none text-[oklch(78%_0.06_155)]"
          title={websiteCategoryLabel(categoryId, t)}
        >
          <Layers size={11} className="shrink-0" aria-hidden />
          <span className="truncate">{websiteCategoryLabel(categoryId, t)}</span>
        </span>
      ))}
    </div>
  );
}

/** Aggregated picker summary for rule cards (text-only). */
export function aggregatedPickerSummary(
  applicationCount: number,
  categoryCount: number,
  webDomainCount: number,
): string | null {
  if (applicationCount + categoryCount + webDomainCount === 0) return null;
  return selectionSummary({
    id: "",
    applicationCount,
    categoryCount,
    webDomainCount,
    updatedAt: 0,
  });
}
